import type { ReactNode } from 'react';

import AuthError from 'components/AuthError';
import Button from 'components/Button';
import { useAuthStatusContext } from 'contexts';
import {
	useIsCompanySite,
	useJulaConnectTokenAuthorizeUrl,
	useLoginOptions,
} from 'hooks';
import { cnm } from 'utils/classNames';
import { getTestDataAttrFrom } from 'utils/helpers';

interface Props {
	becomeMemberButtonClassName?: string;
	becomeMemberButtonText?: string;
	customerNumber?: string;
	importantInformation?: ReactNode;
	introHeading?: string;
	introText?: string;
	onBecomeMemberClick?: () => void;
	onSwitchSiteClick?: () => void;
	switchSiteButtonText?: string;
}

export default function LoginOptions({
	becomeMemberButtonClassName,
	becomeMemberButtonText,
	customerNumber,
	importantInformation,
	introHeading,
	introText,
	onBecomeMemberClick,
	onSwitchSiteClick,
	switchSiteButtonText,
}: Props) {
	const isCompanySite = useIsCompanySite();
	const loginOptions = useLoginOptions();
	const { getAuthorizeUrl } = useJulaConnectTokenAuthorizeUrl();
	const { loginError, resetLoginError } = useAuthStatusContext();

	return (
		<>
			{introHeading && <p className="mb-2 font-bold">{introHeading}</p>}
			{introText && <p className="mb-6">{introText}</p>}

			{importantInformation}

			<AuthError
				className="mb-6"
				error={loginError}
				onDismiss={resetLoginError}
			/>

			<div className="space-y-2">
				{loginOptions?.map((opt) => {
					const href = getAuthorizeUrl({
						customerType: isCompanySite ? 'Pro' : 'Club Staff',
						loginMethod: opt.identifier,
						customerNumber,
					});

					if (!href) {
						return null;
					}

					return (
						<Button
							key={opt.identifier}
							variant="cta"
							displayWidth="full"
							size="lg"
							leadingIcon={opt.icon}
							href={href}
							data-cy={getTestDataAttrFrom(`login-${opt.identifier}`)}
						>
							{opt.text}
						</Button>
					);
				})}
			</div>

			{becomeMemberButtonText && onBecomeMemberClick && (
				<Button
					variant="secondary"
					displayWidth="full"
					size="lg"
					className={cnm('mt-8', becomeMemberButtonClassName)}
					onClick={onBecomeMemberClick}
					data-cy={getTestDataAttrFrom('become-member')}
				>
					{becomeMemberButtonText}
				</Button>
			)}
			{switchSiteButtonText && onSwitchSiteClick && (
				<div className="mt-6 flex justify-center">
					<Button variant="text" className="" onClick={onSwitchSiteClick}>
						{switchSiteButtonText}
					</Button>
				</div>
			)}
		</>
	);
}
LoginOptions.displayName = 'LoginOptions';
